/** 모바일 — 시술기록 (핵심). 저장 시 재고 FIFO 출고 + 회차 차감 + 원가 확정 (PC 시술기록과 동일 규칙) */
import React, { useMemo, useState } from 'react';
import { showAlert } from 'utils/toastBridge';
import {
  loadTable, customerOptions, serviceOptions, activePassesOf, passRemain, serviceItemsOf, saveRecord,
  deleteRecord, stockSummary, nowLocal, num, fmt,
} from 'shop/shopStore';
import { Page, H, CardBox, Field, Btn, Chip } from './mobileUi';

const MobileRecord = ({ tc }) => {
  const [f, setF] = useState({ customerUid: '', serviceUid: '', passUid: '', staffNm: '', remark: '' });
  const [tick, setTick] = useState(0);
  const set = (patch) => setF((p) => ({ ...p, ...patch }));

  const customers = useMemo(() => customerOptions(tc), [tc]);
  const services = useMemo(() => serviceOptions(tc), [tc]);
  const passes = useMemo(
    () => (f.customerUid ? activePassesOf(tc, f.customerUid, f.serviceUid) : []),
    [tc, f.customerUid, f.serviceUid, tick] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const bom = useMemo(() => (f.serviceUid ? serviceItemsOf(tc, f.serviceUid) : []), [tc, f.serviceUid]);
  const stock = useMemo(
    () => new Map(stockSummary(tc).map((s) => [s.productUid, s])),
    [tc, tick] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const recent = useMemo(() => {
    const cs = new Map(loadTable(tc, 'customer').map((c) => [c.customerUid, c]));
    const ss = new Map(loadTable(tc, 'service').map((s) => [s.serviceUid, s]));
    return loadTable(tc, 'record')
      .sort((a, b) => String(b.visitDt).localeCompare(String(a.visitDt)))
      .slice(0, 10)
      .map((r) => ({ ...r, cn: cs.get(r.customerUid)?.customerNm || '-', sn: ss.get(r.serviceUid)?.serviceNm || '-' }));
  }, [tc, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (patch) => {
    const next = { ...f, ...patch };
    const list = next.customerUid ? activePassesOf(tc, next.customerUid, next.serviceUid) : [];
    setF({ ...next, passUid: list.length === 1 ? list[0].passUid : '' });
  };

  const shortage = bom.filter((i) => num(i.useQty) > (stock.get(i.productUid)?.remainQty || 0));

  const submit = () => {
    try {
      const rec = saveRecord(tc, {
        ...f,
        visitDt: nowLocal(),
        items: bom.map((i) => ({ productUid: i.productUid, useQty: i.useQty })),
      });
      const pass = f.passUid ? loadTable(tc, 'pass').find((p) => p.passUid === f.passUid) : null;
      showAlert(`저장했습니다. 원가 ₩${fmt(rec.costAmount)}${pass ? ` · 잔여 ${passRemain(pass)}회` : ''}`, 'success');
      setF((p) => ({ ...p, remark: '' }));
      setTick((t) => t + 1);
    } catch (e) {
      showAlert(e.message || '저장 실패', 'error');
    }
  };

  const remove = (r) => {
    if (!window.confirm(`${r.cn} · ${r.sn} 기록을 삭제하고 재고·회차를 되돌릴까요?`)) return;
    deleteRecord(tc, r.recordUid);
    setTick((t) => t + 1);
  };

  return (
    <Page>
      <H>시술 기록</H>
      <Field>고객
        <select value={f.customerUid} onChange={(e) => pick({ customerUid: e.target.value })}>
          <option value="">선택</option>
          {customers.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field>서비스
        <select value={f.serviceUid} onChange={(e) => pick({ serviceUid: e.target.value })}>
          <option value="">선택</option>
          {services.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field>회원권
        <select value={f.passUid} onChange={(e) => set({ passUid: e.target.value })}>
          <option value="">사용 안 함(단건)</option>
          {passes.map((p) => <option key={p.passUid} value={p.passUid}>잔여 {passRemain(p)}/{p.totalCnt}회</option>)}
        </select>
      </Field>
      <Field>담당 원장
        <input value={f.staffNm} onChange={(e) => set({ staffNm: e.target.value })} />
      </Field>
      <Field>메모
        <input value={f.remark} onChange={(e) => set({ remark: e.target.value })} />
      </Field>
      {bom.length > 0 && (
        <CardBox $warn={shortage.length > 0}>
          <span className="t">사용 제품 (서비스 기본값)</span>
          {bom.map((i) => (
            <span className="s" key={i.itemUid}>
              {stock.get(i.productUid)?.productNm} {num(i.useQty)}{stock.get(i.productUid)?.useUnit} · 재고 {fmt(stock.get(i.productUid)?.remainQty || 0)}
            </span>
          ))}
          {shortage.length > 0 && <span className="t">재고 부족 — 입고 후 저장하세요</span>}
        </CardBox>
      )}
      <Btn $primary disabled={!f.customerUid || !f.serviceUid} onClick={submit}>시술 저장</Btn>
      <H style={{ marginTop: 8 }}>최근 기록</H>
      {recent.length === 0 && <span className="s">기록이 없습니다.</span>}
      {recent.map((r) => (
        <CardBox key={r.recordUid}>
          <span className="t">{r.cn} · {r.sn}</span>
          <span className="s">{r.visitDt}{r.sessionNo ? ` · ${r.sessionNo}회차` : ''}{r.staffNm ? ` · ${r.staffNm}` : ''}</span>
          <span><Chip>원가 ₩{fmt(r.costAmount)}</Chip></span>
          <Btn onClick={() => remove(r)}>삭제(되돌리기)</Btn>
        </CardBox>
      ))}
    </Page>
  );
};

export default MobileRecord;
